import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import useAuthInit from '../../hooks/useAuthInit';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import Pagination from '../../components/common/Pagination';
import SearchInput from '../../components/common/SearchInput';
import { Sparkles, ShieldCheck, Headphones, Radio } from 'lucide-react';


export default function PublicReviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me } = useSelector(authSelector.selectAuthState);
  const { loading: authLoading } = useAuthInit();
  const params = new URLSearchParams(location.search);
  const initialPage = parseInt(params.get('page')) || 1;

  const isAdmin =
    (me?.userType === 'client' && (me?.adminYn === 'Y' || me?.superAdminYn === 'Y')) ||
    (me?.userType === 'member' && me?.reviewAdminYn === 'Y');


  useEffect(() => {
    if (authLoading) return;
    if (!me) {
      navigate(`${PUBLIC_BASE}/login`, { replace: true });
    } else if (me.userType === 'client' && me.adminYn !== 'Y') {
      alert('일반회원 전용 서비스입니다.\n권리사는 정산시스템을 이용해 주세요.');
      navigate(PUBLIC_BASE || '/', { replace: true });
    }
  }, [me, authLoading, navigate]);

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReviews = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/public/reviewApply', {
        params: { page, limit: 10, search: searchKeyword },
      });
      setReviews(res.data.items);
      setTotalPages(res.data.totalPages);
      setCurrentPage(res.data.currentPage);
      setTotalCount(res.data.totalCount);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [searchKeyword]);

  useEffect(() => { if (!authLoading && me) fetchReviews(currentPage); }, [currentPage, fetchReviews, authLoading, me]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    navigate(`${PUBLIC_BASE}/review?page=${newPage}`);
  };

  return (
    <PublicLayout>
      <SEO title="음원심의" description="음원심의 신청 및 진행상황을 확인하세요." path="/review" />

      {/* 히어로 */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-violet-600/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.15),transparent)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-10 sm:pt-20 sm:pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-full text-sky-400 text-xs font-medium mb-6">
            <Sparkles size={13} /> Music Review
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">방송심의</h1>
          <p className="text-slate-400 text-sm sm:text-base">
            {isAdmin ? '접수된 음원심의를 검토하고 처리하세요.' : '음원심의 신청 및 진행상황을 확인하세요.'}
          </p>
          {/* 탭 */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <Headphones size={14} />
              음원심의
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">

        <SearchInput
          placeholder="앨범명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => { setCurrentPage(1); setSearchKeyword(searchTerm); navigate(`${PUBLIC_BASE}/review?page=1`); }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
            <span className="text-sm text-slate-500">데이터 불러오는 중...</span>
          </div>
        ) : (
          <>
            {/* 테이블 */}
            <div className="overflow-x-auto rounded-2xl border border-white/[0.08] mt-4">
              <table className="w-full table-auto text-sm text-center">
                <thead>
                  <tr className="bg-white/[0.03] border-b border-white/[0.08]">
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide">순번</th>
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide">앨범명</th>
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide hidden md:table-cell">아티스트</th>
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide hidden md:table-cell">권리사</th>
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide hidden md:table-cell">신청일</th>
                    <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide">진행상황</th>
                  </tr>
                </thead>
                <tbody className="bg-transparent divide-y divide-white/[0.05]">
                  {reviews.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">신청 내역이 없습니다.</td></tr>
                  ) : reviews.map((r, i) => {
                    const hasWaiting = r.status === 'WAITING';
                    const rowNum = totalCount - ((currentPage - 1) * 10 + i);
                    // 권리사 신청 vs 일반회원 신청 구분
                    const albumName = r.isClientApply ? (r.album?.nameKr || '-') : (r.memberReviewAlbum?.albumName || '-');
                    const artistName = r.isClientApply ? (r.album?.artist?.nameKr || '-') : (r.memberReviewAlbum?.artistName || '-');
                    const clientName = r.isClientApply ? (r.client?.name || '-') : (r.memberReviewAlbum?.clientName || r.member?.name || '-');
                    // 권리사 신청건은 홈페이지 레이아웃 뷰어로, 회원 신청건은 공개 뷰어로
                    const handleClick = () => r.isClientApply
                      ? navigate(`${PUBLIC_BASE}/review/client-apply/${r.id}?page=${currentPage}`)
                      : navigate(`${PUBLIC_BASE}/review/${r.id}?page=${currentPage}`);
                    return (
                      <tr key={r.id}
                        className="hover:bg-white/[0.03] cursor-pointer transition-colors"
                        onClick={handleClick}>
                        <td className="px-3 py-3 text-slate-200">{rowNum}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            {r.isClientApply && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 font-medium shrink-0">권리사</span>
                            )}
                            <span className="font-bold text-sky-400 hover:underline">{albumName}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-200 hidden md:table-cell">{artistName}</td>
                        <td className="px-3 py-3 text-slate-200 hidden md:table-cell">{clientName}</td>
                        <td className="px-3 py-3 text-slate-200 hidden md:table-cell">{r.createdAt?.slice(0, 10)}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            hasWaiting ? 'bg-rose-500/20 text-rose-400' : 'bg-sky-500/20 text-sky-400'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${hasWaiting ? 'bg-rose-400' : 'bg-sky-400'}`} />
                            {hasWaiting ? '심의중' : '심의완료'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  if (isAdmin) { alert('신규 신청은 일반회원만 가능합니다. 권리사는 정산시스템을 이용 해주세요.'); return; }
                  navigate(`${PUBLIC_BASE}/review/write`);
                }}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isAdmin
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-400 hover:to-violet-400 text-white shadow-lg shadow-sky-500/25'
                }`}>
                <ShieldCheck size={16} />
                신규 신청
              </button>
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
